import axios from 'axios';
import Cookie from 'js-cookie';

import {
	broncoURL,
} from 'Vulture/ENV.js';

import {
	dlSessionStartPush,
} from 'Vulture/DataLayer';

import {
	isNarrowScoped,
	isSlideshowScoped,
} from 'Vulture/Permissions';

// LEGACY, ANALYTICS ONLY — do NOT gate on this. An unmapped UUID yields '', so a custom
// role satisfies neither `=== 'ia'` nor `!== 'ia'` sensibly; gating belongs on resolved
// permissions (Vulture/Permissions). Kept solely to keep dlSessionStartPush's `userType`
// stable for existing dashboards — which means analytics still cannot see custom roles.
const PG_UUID_TO_ABBREV = {
	'bccf8794-d545-4bb4-8343-1929cc85b7f5': 'ia',
	'cacbcbc2-e8ea-40ac-98e4-7c0f30b3f083': 'iu',
	'd8d5ef60-e16f-4dda-8921-8720276d635e': 'iu',
	'd97ec83e-d489-441e-ad83-c360382f10a6': 'ea',
	'871b40fb-ec5c-483a-8814-2a5e035bbb99': 'ea',
	'14c4898e-7489-4893-a50c-a4a3fdbc8c7a': 'pm',
	'2f7f224b-e279-11ef-85b8-0242ac120003': 'cm',
};

const pgFromUUID = (uuid) => {
	return PG_UUID_TO_ABBREV[uuid] || '';
};

export const clearSecret = () => {
	document.cookie = 'secret=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
};

export const hasSession = function () {
	let sessionKeyCookie = Cookie.get('session');
	if (sessionKeyCookie) {
		return true;
	}

	return false;
};

export const getSecret = () => {
	if (Cookie.get('secret') === 'undefined') {
		return;
	}

	return Cookie.get('secret');
};

export const getEmail = () => {
	return Cookie.get('email');
};

export const getSession = function () {
	if (!hasSession()) {
		return false;
	}

	refreshSession();

	// No `pg` by design — gate on resolved permissions, not role identity.
	return {
		session:    Cookie.get('session'),
		user:       Cookie.get('user'),
		email:      Cookie.get('email'),
	};
};

// Current selected account. Account-locking on the backend reads this (sent as the
// X-Account-UUID header by the global interceptor, see AccountHeaderInterceptor.js)
// to resolve the user's role/scope FOR the selected account.
export const getCurrentAccountUUID = function () {
	return Cookie.get('account_uuid') || '';
};

// Parent domain for cookie cleanup (e.g. dashboard.touchsource.com -> .touchsource.com).
// A cookie written with a Domain attribute is a DIFFERENT cookie from the host-only one
// we write; if a stale copy exists the browser lists it first and js-cookie returns it,
// so writing the host-only cookie never overrides it. Returns '' on bare/local hosts.
const parentCookieDomain = function () {
	const parts = window.location.hostname.split('.');
	if (parts.length < 3 || (/^\d+$/).test(parts[parts.length - 1])) {
		return '';
	}
	return '.' + parts.slice(1).join('.');
};

// Expire every variant of the account_uuid cookie (host-only and parent-domain).
export const clearCurrentAccountUUID = function () {
	const expired = 'Thu, 01 Jan 1970 00:00:00 GMT';
	document.cookie = `account_uuid=; expires=${expired}; path=/; SameSite=Lax`;
	const domain = parentCookieDomain();
	if (domain) {
		document.cookie = `account_uuid=; expires=${expired}; path=/; domain=${domain}; SameSite=Lax`;
	}
};

export const setCurrentAccountUUID = function (accountUUID) {
	// Always clear first so a stale duplicate can never shadow the value we set —
	// a stale account the user can't access makes bronco fail closed on every
	// scoped write ("<model> not in caller scope").
	clearCurrentAccountUUID();
	if (accountUUID) {
		document.cookie = `account_uuid=${accountUUID}; path=/; SameSite=Lax`;
	}
};

export const setSecret = (secret) => {
	const parsedExpiresAt = new Date();
	parsedExpiresAt.setTime(parsedExpiresAt.getTime() + 5 * 60 * 1000);
	const stringExpiresAt = parsedExpiresAt.toUTCString();

	document.cookie = `secret=${secret}; expires=${stringExpiresAt}; path=/`;
};

export const setEmail = (email) => {
	const parsedExpiresAt = new Date();
	parsedExpiresAt.setTime(parsedExpiresAt.getTime() + 5 * 60 * 1000);
	const stringExpiresAt = parsedExpiresAt.toUTCString();

	document.cookie = `email=${email}; expires=${stringExpiresAt}; path=/`;
};

export const setSession = (session) => {
	let sessionKey = session.session_key;
	let userUUID = session.uuid;
	let expiresAt = session.expires_at;
	let pg = session.pg;

	let parsedExpiresAt = new Date(expiresAt);
	let stringExpiresAt = parsedExpiresAt.toUTCString();

	document.cookie = `session=${sessionKey}; expires=${stringExpiresAt}; path=/ ;SameSite=Lax`;
	document.cookie = `email=${session.email}; expires=${stringExpiresAt}; path=/ ;SameSite=Lax`;
	document.cookie = `pg=${pg}; expires=${stringExpiresAt}; path=/ ;SameSite=Lax`;
	document.cookie = `user=${userUUID}; expires=${stringExpiresAt}; path=/ ;SameSite=Lax`;

	dlSessionStartPush({
		userType: pgFromUUID(Cookie.get('pg') || ''), // see PG_UUID_TO_ABBREV — analytics only

		userUUID,
		internal: session.email.includes('touchsource.com'),
	});
};

export const refreshSession = () => {
	let sessionKey = Cookie.get('session');
	let email = Cookie.get('email');
	let pg = Cookie.get('pg');
	let userUUID = Cookie.get('user');

	const parsedExpiresAt = new Date();
	parsedExpiresAt.setTime(parsedExpiresAt.getTime() + 24 * 60 * 60 * 1000);
	const stringExpiresAt = parsedExpiresAt.toUTCString();

	document.cookie = `session=${sessionKey}; expires=${stringExpiresAt}; path=/ ;SameSite=Lax`;
	document.cookie = `email=${email}; expires=${stringExpiresAt}; path=/ ;SameSite=Lax`;
	document.cookie = `pg=${pg}; expires=${stringExpiresAt}; path=/ ;SameSite=Lax`;
	document.cookie = `user=${userUUID}; expires=${stringExpiresAt}; path=/ ;SameSite=Lax`;
};

export const delSession = function() {
	// Clear browser sessionStorage
	sessionStorage.clear();

	// Clear all session-related cookies
	Cookie.remove('session', {
		path: '/',
	});
	Cookie.remove('email', {
		path: '/',
	});
	Cookie.remove('pg', {
		path: '/',
	});
	Cookie.remove('user', {
		path: '/',
	});

	// Also clear the 'secret' cookie that's managed separately
	clearSecret();

	// For cookies set directly with document.cookie
	// Setting expired date ensures immediate deletion
	const expiredDate = 'Thu, 01 Jan 1970 00:00:00 GMT';
	document.cookie = `session=; expires=${expiredDate}; path=/; SameSite=Lax`;
	document.cookie = `email=; expires=${expiredDate}; path=/; SameSite=Lax`;
	document.cookie = `pg=; expires=${expiredDate}; path=/; SameSite=Lax`;
	document.cookie = `user=; expires=${expiredDate}; path=/; SameSite=Lax`;
	// scope_type is no longer set (scope is derived from permissions); clear any legacy cookie.
	document.cookie = `scope_type=; expires=${expiredDate}; path=/; SameSite=Lax`;
	// Selected account must not outlive the login; a leftover value from another
	// user/account locks the next session to an account they may not have.
	clearCurrentAccountUUID();
	document.cookie = `secret=; expires=${expiredDate}; path=/`;
};

export const login2FA = async function (email, pass) {
	if (hasSession()) {
		return;
	}

	let url = `${broncoURL}/login`;
	let body = {
		email,
		pass,
	};

	try {
		const res = await axios.post(url, body, {});

		return {
			res,
			data: res.data,
		};
	} catch (err) {
		if (axios.isAxiosError(err)) {
			return {
				err:  true,
				res:  err?.response || {},
				data: err?.response?.data || {},
			};
		}

		return {
			err:  true,
			res:  {},
			data: {},
		};
	}
};

export const verify2FA = async function (email, code) {
	let url = `${broncoURL}/verify_2fa`;
	let body = {
		email,
		code,
	};

	try {
		let res = await axios.post(url, body, {	});

		return {
			res,
			data: res.data,
		};
	} catch (err) {
		if (axios.isAxiosError(err)) {
			return {
				err:  true,
				res:  err?.response || {},
				data: err?.response?.data || {},
			};
		}

		return {
			err:  true,
			res:  {},
			data: {},
		};
	}
};

export const logout = async function () {
	if (!hasSession()) {
		return;
	}

	let url = `${broncoURL}/logout`;
	let body = {
		'session_key': getSession().session,
	};

	try {
		let res = await axios.post(url, body, {});

		return res.data;
	} catch (err) {
		console.error(err);
		// no error handler needed
	}
};

// Slideshow-scoped users. Name kept because its call sites read naturally with it.
export const isContentManager = () => {
	if (!hasSession()) {
		return false;
	}

	return isSlideshowScoped();
};

// Users who see a whole account or the whole fleet. Formerly the ia/iu/ea allowlist.
export const isAdministrativeUser = () => {
	if (!hasSession()) {
		return false;
	}

	return !isNarrowScoped();
};